import { TFunction } from 'i18next';
import React, { useCallback, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { validate } from 'uuid';

import { CreateDroneHarmonyFlightPlanRequestBody } from '../../../../../../../../../typings/api/skymap/rest/v1/project';
import { TokenService } from '../../../../../../../services/authentication/token.service';
import {
  FileUpload,
  UploadManuallyStoppedError,
} from '../../../../../../../services/network/file-upload';
import { WebApi } from '../../../../../../../services/web-api';
import { FeatureFlagsManager } from '../../../../../../../utils/core/feature-flags-manager';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { ProjectContext } from '../../../../../../state/project-state/project-state';
import { Button } from '../../../../../button/button';
import { supportedBoundaryPolygonFileTypes } from '../../../../../cloud-processing/gcp-page/boundary-polygon-list';
import { GcpRow } from '../../../../../cloud-processing/gcp-parser';
import { GcpFileFormElement } from '../../../../../cloud-processing/geodata-configuration/gcp-file-form-element';
import { Dialog } from '../../../../../dialog/dialog';
import { FileSelectionButton } from '../../../../../file-selection-button/file-selection-button';
import { Icon } from '../../../../../icon/icon';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Select } from '../../../../../select/select';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { ProcessingPackagesInfo } from './processing-packages-info';
import { TerrainModelInfo } from './terrain-model-info';
import { useSaveQuery } from './use-save-query';

type GeodataProcessType = CreateDroneHarmonyFlightPlanRequestBody['package']['geodataProcessType'];

export type FormValues = {
  gcpFilename: string | undefined | null;
  generateTerrainModel: boolean;
  geodataProcessType: GeodataProcessType;
  missionId: string;
};

type UploadedBoundaryFile = {
  id: string;
  fileName: string;
  uploadId?: string;
  isUploading: boolean;
};

type BoundaryFileItemProps = {
  fileName: string;
  isUploading: boolean;
  onRemove: () => void;
};

const BoundaryFileItem = ({ fileName, isUploading, onRemove }: BoundaryFileItemProps) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <FileName>{fileName}</FileName>
      <Icon
        fixedWidth={true}
        icon={['fal', isUploading ? 'spinner' : 'trash-can']}
        size="sm"
        spin={isUploading}
        style={{ cursor: 'pointer' }}
        title={isUploading ? t('uploading', { ns: 'common' }) : t('delete', { ns: 'common' })}
        onClick={onRemove}
      />
    </Stack>
  );
};

const getDataProcessTypes = (t: TFunction) => {
  const values: { name: string; id: GeodataProcessType }[] = [
    {
      id: 'ORTHO_ONLY',
      name: t('setupDialog.cards.orthophoto.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'FULL',
      name: t('setupDialog.cards.standard.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'POINTCLOUD_ONLY',
      name: t('setupDialog.cards.pointCloud.title', { ns: 'cloudProcessing' }),
    },
    {
      id: 'FULL_HIGH_QUALITY',
      name: t('setupDialog.cards.premium.title', { ns: 'cloudProcessing' }),
    },
  ];

  if (FeatureFlagsManager.instance.isEnabled('SK-6613-signature-processing-package')) {
    values.push({
      id: 'SIGNATURE',
      name: t('setupDialog.cards.signature.title', { ns: 'cloudProcessing' }),
    });
  }

  if (FeatureFlagsManager.instance.isEnabled('SK-6920-pm')) {
    values.push({
      id: 'PM',
      name: 'WIP-2026',
    });
  }

  return values;
};

const getGenerateTerrainModelItems = (t: TFunction) => [
  {
    id: true,
    name: t('yes', { ns: 'common' }),
  },
  {
    id: false,
    name: t('no', { ns: 'common' }),
  },
];

type Props = {
  onClose: () => void;
};

const AddFlightPlanDialog = (props: Props) => {
  const { t } = useTranslation();
  const generateTerrainModelItemsRef = useRef(getGenerateTerrainModelItems(t));
  const geodataProcessTypesRef = useRef(getDataProcessTypes(t));
  const { project } = React.useContext(ProjectContext);
  const gcpPointsRef = React.useRef<GcpRow[]>([]);
  const { handleError } = useErrorHandling();
  const [uploadedBoundaryFiles, setUploadedBoundaryFiles] = useState<UploadedBoundaryFile[]>([]);
  const { saveQuery, getErrorForPath, errorList } = useSaveQuery({
    gcpFileContent: gcpPointsRef.current,
    projectId: project.id,
    boundaryPolygonUploadIds: uploadedBoundaryFiles
      .filter((f) => f.uploadId)
      .map((f) => f.uploadId!),
    onSuccess: props.onClose,
  });

  const form = useForm<FormValues>({
    defaultValues: {
      missionId: '',
      geodataProcessType: 'ORTHO_ONLY',
      gcpFilename: undefined,
      generateTerrainModel: true,
    },
  });

  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    register,
    setError,
    setFocus,
    trigger,
    watch,
  } = form;

  const onBoundaryFilesSelected = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const fileKey = `${file.name}_${file.size}_${file.lastModified}`;

        // Add file to the list immediately with uploading state
        setUploadedBoundaryFiles((prev) => [
          ...prev,
          {
            id: fileKey,
            fileName: file.name,
            isUploading: true,
          },
        ]);

        try {
          // Upload the file
          const upload = new FileUpload({
            apiBaseUrl: WebApi.getUrl(),
            authToken: new TokenService().get(),
            file: file,
            concurrency: 1,
          });

          const { id: uploadId } = await upload.start();

          // Update the file with upload ID and mark as completed
          setUploadedBoundaryFiles((prev) =>
            prev.map((f) => (f.id === fileKey ? { ...f, uploadId, isUploading: false } : f)),
          );
        } catch (error) {
          console.error('Failed to upload boundary polygon file:', error);
          if (error instanceof UploadManuallyStoppedError) {
            // Remove the file from the list if upload was manually stopped
            setUploadedBoundaryFiles((prev) => prev.filter((f) => f.id !== fileKey));
            return;
          }
          handleError(error);
          // Remove the file from the list on error
          setUploadedBoundaryFiles((prev) => prev.filter((f) => f.id !== fileKey));
        }
      }
    },
    [handleError],
  );

  const removeBoundaryFile = useCallback((fileId: string) => {
    setUploadedBoundaryFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  React.useEffect(() => {
    setFocus('missionId');
  }, [setFocus]);

  React.useEffect(() => {
    const gcpError = getErrorForPath('body.groundControlPoints');
    if (gcpError) {
      setError('gcpFilename', {
        type: 'custom',
        message: gcpError,
      });
    } else {
      clearErrors('gcpFilename');
    }
    // TODO: Make backend throw nested levels. For instance these two:
    // TODO: body.package.generateTerrainModel and body.package.geodataProcessType, if in future
    // TODO: there are custom guards for these two properties.
    const packageError = getErrorForPath('body.package');
    if (packageError) {
      setError('generateTerrainModel', {
        type: 'custom',
        message: packageError,
      });
      setError('geodataProcessType', {
        type: 'custom',
        message: packageError,
      });
    } else {
      clearErrors('generateTerrainModel');
      clearErrors('geodataProcessType');
    }
    const missionIdError = getErrorForPath('body.missionId');
    if (missionIdError) {
      setError('missionId', {
        type: 'custom',
        message: missionIdError,
      });
    } else {
      clearErrors('missionId');
    }
  }, [clearErrors, getErrorForPath, setError]);

  return (
    <>
      <Dialog width={500} onClose={props.onClose}>
        {{
          header: t('addFlightPlanDialog.title', { ns: 'cloudProcessing' }),
          content: (
            <form id="addFlightPlanForm" onSubmit={handleSubmit((form) => saveQuery.mutate(form))}>
              <Stack spacing={1}>
                <LabelledContainer
                  required={true}
                  text={t('addFlightPlanDialog.id', { ns: 'cloudProcessing' })}
                >
                  {(formElementId) => (
                    <TextBox
                      {...register('missionId', {
                        required: true,
                        validate: (val) => validate(val) || t('invalidValue', { ns: 'common' }),
                      })}
                      disabled={saveQuery.isPending}
                      errorMessage={formState.errors.missionId?.message}
                      id={formElementId()}
                      placeholder="00000000-0000-0000-0000-000000000000"
                    />
                  )}
                </LabelledContainer>

                <LabelledContainer
                  infoTooltip={
                    <ProcessingPackagesInfo geodataProcessType={watch('geodataProcessType')} />
                  }
                  required={true}
                  text={t('setupDialog.cards.processingPackage', { ns: 'cloudProcessing' })}
                >
                  {(formElementId) => {
                    const id = formElementId();

                    return (
                      <Controller
                        control={control}
                        name="geodataProcessType"
                        render={({ field }) => (
                          <Select
                            {...field}
                            disabled={saveQuery.isPending}
                            errorMessage={formState.errors.geodataProcessType?.message}
                            id={id}
                            options={geodataProcessTypesRef.current}
                            placeholderText={t('notSelected', { ns: 'common' })}
                          />
                        )}
                        rules={{
                          required: true,
                        }}
                      />
                    );
                  }}
                </LabelledContainer>

                <LabelledContainer
                  infoTooltip={<TerrainModelInfo />}
                  required={true}
                  text={t('setupDialog.addOns.terrainModel.generateTerrainModel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  {(formElementId) => {
                    const id = formElementId();

                    return (
                      <Controller
                        control={control}
                        name="generateTerrainModel"
                        render={({ field }) => (
                          <Select
                            {...field}
                            disabled={
                              saveQuery.isPending || watch('geodataProcessType') === 'ORTHO_ONLY'
                            }
                            errorMessage={formState.errors.generateTerrainModel?.message}
                            id={id}
                            options={generateTerrainModelItemsRef.current}
                            onChange={(e) => {
                              field.onChange(e.target.value === 'true');
                              void trigger('generateTerrainModel');
                            }}
                          />
                        )}
                      />
                    );
                  }}
                </LabelledContainer>

                <GcpFileFormElement
                  disabled={saveQuery.isPending}
                  form={form}
                  gcpPointsRef={gcpPointsRef}
                  required={false}
                />

                <LabelledContainer text={t('boundaryPolygonList.title', { ns: 'cloudProcessing' })}>
                  <Stack spacing={0.5}>
                    <FileSelectionButton
                      accept={supportedBoundaryPolygonFileTypes}
                      disabled={saveQuery.isPending}
                      leftIcon={{ icon: ['fad', 'file-upload'] }}
                      multiple={true}
                      text={t('boundaryPolygonList.addBoundaryPolygons', {
                        ns: 'cloudProcessing',
                      })}
                      variant="contained"
                      onFilesSelected={onBoundaryFilesSelected}
                    />

                    {uploadedBoundaryFiles.length > 0 && (
                      <Stack spacing={0.5}>
                        {uploadedBoundaryFiles.map((file) => (
                          <BoundaryFileItem
                            fileName={file.fileName}
                            isUploading={file.isUploading}
                            key={file.id}
                            onRemove={() => removeBoundaryFile(file.id)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </LabelledContainer>

                {errorList}
              </Stack>
            </form>
          ),
          footer: {
            right: (
              <>
                <Button
                  color="primary"
                  disabled={saveQuery.isPending || uploadedBoundaryFiles.some((f) => f.isUploading)}
                  form="addFlightPlanForm"
                  type="submit"
                  variant="contained"
                >
                  {t('add', { ns: 'common' })}
                </Button>
                <Button variant="text" onClick={props.onClose}>
                  {t('close', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    </>
  );
};

const FileName = styled.span`
  font-size: 0.9em;
  color: ${({ theme }) => theme.color.gray.darkest};
  flex: 1;
  margin-right: 0.5em;
`;

export { AddFlightPlanDialog };
